<template>
  <div class="empresas-nueva">
    <div class="content-header">
      <h1 class="content-header-title">Nueva Empresa</h1>
      <p class="content-header-subtitle">Crear una nueva empresa</p>
    </div>
    
    <b-card>
      <b-form @submit.prevent="guardarEmpresa">
        <b-row>
          <!-- Información básica -->
          <b-col md="6">
            <h5 class="section-title">Información Básica</h5>
            
            <b-form-group label="Nombre de la Empresa" required>
              <b-form-input 
                v-model="empresa.nombre" 
                placeholder="Ingrese el nombre de la empresa"
                required 
                :state="validaciones.nombre"
              />
              <b-form-invalid-feedback v-if="!validaciones.nombre">
                El nombre es obligatorio
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group label="CIF" required>
              <b-form-input 
                v-model="empresa.cif" 
                placeholder="Ingrese el CIF de la empresa"
                required 
                :state="validaciones.cif"
                @blur="validarCif"
              />
              <b-form-invalid-feedback v-if="!validaciones.cif">
                {{ errores.cif || 'El CIF es obligatorio' }}
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group label="Tipo de Empresa" required>
              <b-form-select 
                v-model="empresa.idTipoEmpresa" 
                :options="tiposEmpresa"
                value-field="idTipoEmpresa"
                text-field="nombre"
                placeholder="Seleccione un tipo de empresa"
                required
                :state="validaciones.tipoEmpresa"
              />
              <b-form-invalid-feedback v-if="!validaciones.tipoEmpresa">
                El tipo de empresa es obligatorio
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group label="Dirección" required>
              <b-form-textarea 
                v-model="empresa.direccion" 
                placeholder="Ingrese la dirección de la empresa"
                rows="3"
                required 
                :state="validaciones.direccion"
              />
              <b-form-invalid-feedback v-if="!validaciones.direccion">
                La dirección es obligatoria
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>

          <!-- Información de contacto -->
          <b-col md="6">
            <h5 class="section-title">Información de Contacto</h5>
            
            <b-form-group label="Email">
              <b-form-input 
                v-model="empresa.email" 
                type="email"
                placeholder="email@empresa.com"
                :state="validaciones.email"
                @blur="validarEmail"
              />
              <b-form-invalid-feedback v-if="!validaciones.email">
                {{ errores.email || 'Formato de email inválido' }}
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group label="Teléfono">
              <b-form-input 
                v-model="empresa.telefono" 
                placeholder="+34 123 456 789"
                :state="validaciones.telefono"
              />
            </b-form-group>

            <b-form-group label="Página Web">
              <b-form-input 
                v-model="empresa.web" 
                placeholder="https://www.empresa.com"
                :state="validaciones.web"
                @blur="validarWeb"
              />
              <b-form-invalid-feedback v-if="!validaciones.web">
                {{ errores.web || 'Formato de URL inválido' }}
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group label="Observaciones">
              <b-form-textarea 
                v-model="empresa.observaciones" 
                placeholder="Observaciones adicionales sobre la empresa"
                rows="4"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Sección de contactos -->
        <hr class="my-4">
        <div class="contactos-section">
          <h5 class="section-title">Contactos de la Empresa</h5>
          <p class="text-muted mb-3">
            Puedes agregar contactos después de crear la empresa, o hacerlo ahora si ya tienes la información.
          </p>
          
          <!-- Mostrar mensaje si no hay empresa creada aún -->
          <div v-if="!empresaCreada" class="alert alert-info">
            <feather-icon icon="InfoIcon" size="16" class="mr-2" />
            Primero debes crear la empresa para poder agregar contactos.
          </div>
          
          <!-- Componente de gestión de contactos -->
          <GestionContactos 
            v-if="empresaCreada && empresa.idEmpresa" 
            :id-empresa="empresa.idEmpresa"
            @contacto-creado="onContactoCreado"
            @contacto-actualizado="onContactoActualizado"
            @contacto-eliminado="onContactoEliminado"
          />
        </div>

        <!-- Botones de acción -->
        <div class="form-actions">
          <b-button 
            type="button" 
            variant="outline-secondary" 
            @click="cancelar"
            class="mr-2"
          >
            {{ empresaCreada ? 'Ver Empresa' : 'Cancelar' }}
          </b-button>
          <b-button 
            v-if="!empresaCreada"
            type="submit" 
            variant="primary" 
            :disabled="!formularioValido || guardando"
          >
            <b-spinner v-if="guardando" small class="mr-1" />
            {{ guardando ? 'Guardando...' : 'Guardar Empresa' }}
          </b-button>
          <b-button 
            v-if="empresaCreada"
            type="button" 
            variant="success" 
            @click="finalizarCreacion"
          >
            <feather-icon icon="CheckIcon" size="16" class="mr-1" />
            Finalizar
          </b-button>
        </div>
      </b-form>
    </b-card>
  </div>
</template>

<script>
import { EmpresaService } from '@/services/empresaService'
import { TipoEmpresaService } from '@/services/tipoEmpresaService'
import { Empresa, TipoEmpresa } from '@/types'
import GestionContactos from '@/components/GestionContactos.vue'

export default {
  name: 'EmpresasNueva',
  components: {
    GestionContactos
  },
  data() {
    return {
      empresa: {
        idEmpresa: null,
        nombre: '',
        cif: '',
        direccion: '',
        idTipoEmpresa: null,
        web: '',
        telefono: '',
        email: '',
        observaciones: ''
      },
      tiposEmpresa: [],
      guardando: false,
      empresaCreada: false,
      validaciones: {
        nombre: null,
        cif: null,
        direccion: null,
        tipoEmpresa: null,
        email: null,
        web: null
      },
      errores: {
        cif: '',
        email: '',
        web: ''
      }
    }
  },
  computed: {
    formularioValido() {
      return this.validaciones.nombre && 
             this.validaciones.cif && 
             this.validaciones.direccion && 
             this.validaciones.tipoEmpresa &&
             this.validaciones.email !== false &&
             this.validaciones.web !== false
    }
  },
  async mounted() {
    await this.cargarTiposEmpresa()
    this.validarFormulario()
  },
  methods: {
    async cargarTiposEmpresa() {
      try {
        this.tiposEmpresa = await TipoEmpresaService.getAll()
      } catch (error) {
        this.$toast.error('Error al cargar los tipos de empresa')
        console.error('Error:', error)
      }
    },
    validarFormulario() {
      // Validar nombre
      this.validaciones.nombre = this.empresa.nombre && this.empresa.nombre.trim().length > 0
      
      // Validar CIF
      this.validaciones.cif = this.empresa.cif && this.empresa.cif.trim().length > 0
      
      // Validar dirección
      this.validaciones.direccion = this.empresa.direccion && this.empresa.direccion.trim().length > 0
      
      // Validar tipo de empresa
      this.validaciones.tipoEmpresa = this.empresa.idTipoEmpresa !== null
      
      // Validar email (opcional pero si se proporciona debe ser válido)
      if (this.empresa.email && this.empresa.email.trim()) {
        this.validaciones.email = this.validarEmailFormato(this.empresa.email)
      } else {
        this.validaciones.email = null
      }
      
      // Validar web (opcional pero si se proporciona debe ser válida)
      if (this.empresa.web && this.empresa.web.trim()) {
        this.validaciones.web = this.validarWebFormato(this.empresa.web)
      } else {
        this.validaciones.web = null
      }
    },
    validarCif() {
      if (this.empresa.cif && this.empresa.cif.trim()) {
        // Verificar si el CIF ya existe
        this.verificarCifExistente()
      }
    },
    async verificarCifExistente() {
      try {
        const existe = await EmpresaService.existsByCif(this.empresa.cif)
        if (existe) {
          this.validaciones.cif = false
          this.errores.cif = 'Ya existe una empresa con este CIF'
        } else {
          this.validaciones.cif = true
          this.errores.cif = ''
        }
      } catch (error) {
        console.error('Error al verificar CIF:', error)
        this.validaciones.cif = true // Permitir continuar si hay error en la verificación
      }
    },
    validarEmail() {
      if (this.empresa.email && this.empresa.email.trim()) {
        this.validaciones.email = this.validarEmailFormato(this.empresa.email)
        if (!this.validaciones.email) {
          this.errores.email = 'Formato de email inválido'
        } else {
          this.errores.email = ''
        }
      } else {
        this.validaciones.email = null
        this.errores.email = ''
      }
    },
    validarWeb() {
      if (this.empresa.web && this.empresa.web.trim()) {
        this.validaciones.web = this.validarWebFormato(this.empresa.web)
        if (!this.validaciones.web) {
          this.errores.web = 'Formato de URL inválido'
        } else {
          this.errores.web = ''
        }
      } else {
        this.validaciones.web = null
        this.errores.web = ''
      }
    },
    validarEmailFormato(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return emailRegex.test(email)
    },
    validarWebFormato(web) {
      try {
        // Asegurar que tenga protocolo
        const url = web.startsWith('http') ? web : `https://${web}`
        new URL(url)
        return true
      } catch {
        return false
      }
    },
    async guardarEmpresa() {
      this.validarFormulario()
      
      if (!this.formularioValido) {
        this.$toast.error('Por favor, corrige los errores en el formulario')
        return
      }

      this.guardando = true
      
      try {
        // Preparar datos para envío
        const datosEmpresa = {
          ...this.empresa,
          web: this.empresa.web && this.empresa.web.trim() ? this.empresa.web.trim() : undefined,
          telefono: this.empresa.telefono && this.empresa.telefono.trim() ? this.empresa.telefono.trim() : undefined,
          email: this.empresa.email && this.empresa.email.trim() ? this.empresa.email.trim() : undefined,
          observaciones: this.empresa.observaciones && this.empresa.observaciones.trim() ? this.empresa.observaciones.trim() : undefined
        }

        const empresaCreada = await EmpresaService.create(datosEmpresa)
        this.empresa.idEmpresa = empresaCreada.idEmpresa
        this.empresaCreada = true
        this.$toast.success('Empresa creada correctamente. Ahora puedes agregar contactos.')
      } catch (error) {
        this.$toast.error('Error al crear la empresa')
        console.error('Error:', error)
      } finally {
        this.guardando = false
      }
    },
    cancelar() {
      if (this.empresaCreada) {
        // Si ya se creó la empresa, ir al detalle
        this.$router.push({ 
          name: 'empresas-detalle', 
          params: { id: this.empresa.idEmpresa } 
        })
      } else {
        // Si no se creó, volver a la lista
        this.$router.push({ name: 'empresas-lista' })
      }
    },
    onContactoCreado(contacto) {
      this.$toast.success(`Contacto ${contacto.nombre} agregado correctamente`)
    },
    onContactoActualizado(contacto) {
      this.$toast.success(`Contacto ${contacto.nombre} actualizado correctamente`)
    },
    onContactoEliminado(contacto) {
      this.$toast.success(`Contacto ${contacto.nombre} eliminado correctamente`)
    },
    finalizarCreacion() {
      this.$router.push({ 
        name: 'empresas-detalle', 
        params: { id: this.empresa.idEmpresa } 
      })
    }
  },
  watch: {
    empresa: {
      handler() {
        this.validarFormulario()
      },
      deep: true
    }
  }
}
</script>

<style scoped>
.content-header {
  margin-bottom: 2rem;
}

.content-header-title {
  margin-bottom: 0.5rem;
  font-size: 1.75rem;
  font-weight: 600;
}

.content-header-subtitle {
  color: #6c757d;
  margin-bottom: 0;
}

.section-title {
  color: #495057;
  font-weight: 600;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e9ecef;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px solid #e9ecef;
}

.form-actions .btn {
  min-width: 120px;
}

.contactos-section {
  background-color: #f8f9fa;
  border-radius: 0.375rem;
  padding: 1.5rem;
  border: 1px solid #e9ecef;
}

.contactos-section .section-title {
  color: #495057;
  font-weight: 600;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #dee2e6;
}
</style>
